// ══════════════════════════════════════════════════════
// Composants UI partagés
// ══════════════════════════════════════════════════════

import { useState, useEffect } from "react";

/** Compteur animé */
export function CountUp({ value, duration = 1000 }) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCurrent(Math.round(ease * value));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value, duration]);
  return <>{current}</>;
}

/** Carte métrique */
export function MetricCard({ icon, label, value, trend, trendType = "up", sub, color, delay = 0 }) {
  return (
    <div className={`metric-card mc-${color} anim-fade-up`} style={{ animationDelay: `${delay}s` }}>
      <div className="metric-glow" />
      <div className="metric-icon"><i className={`bi ${icon}`} /></div>
      <div className="metric-label">{label}</div>
      <div className="metric-value"><CountUp value={value} /></div>
      {trend && (
        <div className={`metric-trend trend-${trendType}`}>
          <i className={`bi bi-arrow-${trendType === "up" ? "up" : "down"}-right`} />
          {trend} <span>{sub}</span>
        </div>
      )}
    </div>
  );
}

/** Badge priorité */
export function PriorityBadge({ priority }) {
  const map = { CRITICAL: ["bp-critical","Critique"], HIGH: ["bp-high","Haute"], MEDIUM: ["bp-medium","Moyen"], LOW: ["bp-low","Basse"] };
  const [cls, label] = map[priority] || ["bp-low", priority];
  return <span className={`badge-prio ${cls}`}><i className="bi bi-circle-fill" style={{ fontSize: 6 }} />{label}</span>;
}

/** Badge statut ticket */
export function TicketStatusBadge({ status }) {
  const map = {
    OPEN:           ["bs-open",     "Ouvert"],
    IN_PROGRESS:    ["bs-progress", "En cours"],
    PENDING_CLIENT: ["bs-pending",  "En attente"],
    RESOLVED:       ["bs-resolved", "Résolu"],
    CLOSED:         ["bs-closed",   "Fermé"],
    CANCELLED:      ["bs-closed",   "Annulé"],
  };
  const [cls, label] = map[status] || ["bs-open", status];
  return <span className={`badge-status ${cls}`}>{label}</span>;
}

/** Badge statut asset */
export function AssetStatusBadge({ status }) {
  const map = { ONLINE: ["bs-online","En ligne"], OFFLINE: ["bs-offline","Hors ligne"], UNKNOWN: ["bs-unknown","Inconnu"] };
  const [cls, label] = map[status] || ["bs-unknown", status];
  return (
    <span className={`badge-status ${cls}`}>
      <i className={`bi bi-circle-fill me-1`} style={{ fontSize: 7 }} />{label}
    </span>
  );
}

/** Modal générique */
export function Modal({ title, onClose, children, footer }) {
  return (
    <div className="itm-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="itm-modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/** Pagination simple */
export function Pagination({ page, total, pageSize = 25, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    const start = Math.max(0, Math.min(page - 2, totalPages - 5));
    return start + i;
  });
  return (
    <div className="itm-pagination">
      <div className={`page-btn ${page === 0 ? "disabled" : ""}`} onClick={() => page > 0 && onChange(page - 1)}>
        <i className="bi bi-chevron-left" style={{ fontSize: 11 }} />
      </div>
      {pages.map(p => (
        <div key={p} className={`page-btn ${p === page ? "active" : ""}`} onClick={() => onChange(p)}>
          {p + 1}
        </div>
      ))}
      <div className={`page-btn ${page >= totalPages - 1 ? "disabled" : ""}`} onClick={() => page < totalPages - 1 && onChange(page + 1)}>
        <i className="bi bi-chevron-right" style={{ fontSize: 11 }} />
      </div>
      <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: 6 }}>
        {total} résultat{total > 1 ? "s" : ""}
      </span>
    </div>
  );
}

/** Topbar page avec titre */
export function PageTopbar({ title, subtitle, admin, actions, rightContent }) {
  return (
    <>
      <div className="topbar">
        <div className="topbar-title">{title} {subtitle && <span>{subtitle}</span>}</div>
        <div className="search-box">
          <i className="bi bi-search" style={{ color: "var(--text-3)", fontSize: 14 }} />
          <input type="text" placeholder="Rechercher…" />
        </div>
        <div className="topbar-actions">
          {rightContent}
          <div className="topbar-btn"><i className="bi bi-bell" /><span className="notif-dot" /></div>
          <div className="avatar-sm">JD</div>
        </div>
      </div>
    </>
  );
}

/** Barre de filtre */
export function FilterBar({ children }) {
  return <div className="filter-bar">{children}</div>;
}

/** Onglets */
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="itm-tabs">
      {tabs.map(t => (
        <div key={t.id} className={`itm-tab ${active === t.id ? "active" : ""}`} onClick={() => onChange(t.id)}>
          {t.label}
          {t.count !== undefined && (
            <span style={{ marginLeft: 6, background: "var(--navy-soft)", borderRadius: 20, padding: "1px 7px", fontSize: 11, color: "var(--text-2)" }}>
              {t.count}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Barre de progression */
export function ProgressBar({ value, max, color = "var(--accent)" }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="itm-progress" style={{ flex: 1 }}>
      <div className="itm-progress-bar" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/** État vide */
export function EmptyState({ icon = "bi-inbox", message = "Aucun résultat" }) {
  return (
    <div className="empty-state">
      <i className={`bi ${icon}`} />
      <p>{message}</p>
    </div>
  );
}

/** Chip d'alerte */
export function AlertChip({ type = "danger", icon, title, sub }) {
  return (
    <div className={`alert-chip alert-chip-${type}`}>
      <i className={`bi ${icon}`} style={{ color: type === "danger" ? "var(--danger)" : "var(--accent)", fontSize: 16 }} />
      <div className="alert-chip-text">
        <strong>{title}</strong>
        {sub}
      </div>
    </div>
  );
}
